import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { postOpenChest, getChestStatus } from "@/api/chest";
import { sdk } from "@farcaster/miniapp-sdk";
import { UserContext } from "@/types/user";
import { useAccount } from "wagmi";
import { CHEST_SHARE_TEXT } from "@/constants/share";
import { MINIAPP_SHARE_URL } from "@/constants/url";
import { DEMO_USER } from "@/constants/demo";
import toast from "react-hot-toast";
import style from "./style.module.scss";

export default function MysteryBox() {
  const [remaining, setRemaining] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { isConnected } = useAccount();
  const [user, setUser] = useState<UserContext>();
  const [claimed, setClaimed] = useState(false);
  const [opened, setOpened] = useState(false);
  const [point, setPoint] = useState(0);
  const [shared, setShared] = useState(false);
  const connected = import.meta.env.PROD ? isConnected : true;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const context = import.meta.env.PROD
          ? (await sdk.context).user
          : DEMO_USER;
        setUser(context);
        const data = await getChestStatus(context.fid);
        setClaimed(data.claimed);
        setOpened(data.opened);
        setRemaining(calculateRemainingToNextUTC());
      } catch (err) {
        console.error("Unable to load user data:", err);
        toast.error("Unable to load user data. Try again later", {
          duration: 3000,
        });
      }
    };

    fetchStatus();
  }, []);

  // Calculate time to 0h UTC tomorrow
  const calculateRemainingToNextUTC = () => {
    const now = new Date();
    const tomorrowUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    );
    return tomorrowUTC.getTime() - now.getTime();
  };

  // Countdown every seconds
  useEffect(() => {
    if (remaining <= 0) return;

    const timer = setInterval(() => {
      setRemaining(calculateRemainingToNextUTC());
    }, 1000);

    return () => clearInterval(timer);
  }, [remaining]);

  const handleOpen = async () => {
    if (!user || !claimed) return;

    try {
      const { fid } = user;
      const data = await postOpenChest(fid);
      setOpened(true);
      setPoint(data.point);
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Something went wrong:", err);
      toast.error("Something went wrong. Please try again", {
        duration: 3000,
      });
    }
  };

  const handleShare = async () => {
    await sdk.actions.composeCast({
      text: CHEST_SHARE_TEXT,
      embeds: [MINIAPP_SHARE_URL],
    });
    setShared(true);
  };

  const closeModal = () => {
    setShowSuccessModal(false);
  };

  const formatTime = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;

    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const renderCountdown = () => {
    if (!connected) return null;

    if (claimed) {
      if (opened && remaining > 0) {
        return (
          <div className={style.countdown}>
            Next box available in: <strong>{formatTime(remaining)}</strong>
          </div>
        );
      }
    }
    return null;
  };

  const renderOpenBtn = () => {
    if (!connected) return null;
    if (opened) return null;

    if (!shared) {
      return (
        <button
          className="button-flat"
          onClick={handleShare}
          style={{ minWidth: "100px", marginBottom: "40px" }}
          disabled={!claimed}
        >
          Share to open
        </button>
      );
    }

    return (
      <button
        className="button-flat"
        onClick={handleOpen}
        style={{ minWidth: "100px", marginBottom: "40px" }}
        disabled={!claimed}
      >
        Open
      </button>
    );
  };

  return (
    <div className={style.wrapper}>
      <div className={style.titleWrapper}>
        <h2 className={style.heroTitle}>Mystery Box</h2>
        <p className={style.desc}>
          Tap to open – get random tokens from 50 to 1000 $Kawaii. Complete
          preclaim to unlock.
        </p>
      </div>

      <div className={style.videoWrapper}>
        <video
          className={style.video}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        >
          <source src="/video/open-box.mp4" type="video/mp4" />
        </video>
      </div>

      {renderCountdown()}
      {renderOpenBtn()}

      <AnimatePresence>
        {showSuccessModal && user && (
          <>
            <motion.div
              className={style.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            />

            <motion.div
              className={style.modal}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.25 }}
            >
              <h2>🎉 Congrats {user.displayName}</h2>
              <p>You’ve unlocked {point} $Kawaii</p>

              <button
                className="button-flat"
                style={{ borderRadius: "4px", width: "100%" }}
                onClick={closeModal}
              >
                Close
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
